// src/features/authV2/ui/VkOneTapButton.tsx

import { generatePkcePair } from "@features/authV2/lib/pkce.ts";
import { AppConfig } from "@fxid/shared";
import { joinURL } from "@shared/lib/Functions/urls.ts";
import * as VKID from "@vkid/sdk";
import { ConfigAuthMode } from "@vkid/sdk";
import { OneTapParams } from "@vkid/sdk/dist-sdk/types/widgets/oneTap/types";
import log from "loglevel";
import { useEffect, useRef } from "react";
import { v4 as uuidv4 } from "uuid";

import { VkLoginPayload } from "../lib/sdk.ts";

const VK_CONTAINER_ID = "vk-auth-container";

/**
 * @interface VkOneTapButtonProps
 * @description Пропсы для компонента кнопки VK One-Tap.
 * @property {number} appId - ID приложения VK.
 * @property {function} onSuccess - Колбэк при успешной авторизации.
 * @property {function} onError - Колбэк при ошибке.
 */
interface VkOneTapButtonProps {
	appId: number;
	scope?: string | null;
	showOkLogin: boolean;
	showMailRuLogin: boolean;
	onSuccess: (data: VkLoginPayload) => void;
	onError: (error: Error) => void;
}

/**
 * Компонент, отвечающий за рендеринг и логику кнопки VK ID One-Tap.
 */
export const VkOneTapButton = ({
	appId,
	showMailRuLogin,
	showOkLogin,
	scope,
	onSuccess,
	onError
}: VkOneTapButtonProps) => {
	const oneTapRef = useRef<VKID.OneTap | null>(null);
	const codeVerifierRef = useRef<string | null>(null);

	useEffect(() => {
		let isMounted = true;
		log.debug("VkOneTapButton mounted, initializing VK ID SDK");

		const initializeVkId = async () => {
			try {
				const { verifier, challenge } = await generatePkcePair();
				if (!isMounted) return;

				codeVerifierRef.current = verifier;

				// 1. Инициализация конфигурации VK ID
				const config: Parameters<typeof VKID.Config.init>[0] = {
					app: appId,
					redirectUrl: joinURL(AppConfig.apiBasePath, "/api/v1/auth/authenticate_to_fxid"),
					state: uuidv4(),
					codeChallenge: challenge,
					mode: ConfigAuthMode.InNewWindow,
					responseMode: VKID.ConfigResponseMode.Callback
				};

				if (scope != null) {
					config.scope = scope;
				}

				VKID.Config.init(config);
				log.debug("VKID.Config initialized with id %s. Config: %o", appId, config);

				// 2. Создание экземпляра OneTap
				const oneTap = new VKID.OneTap();
				oneTapRef.current = oneTap;
				log.debug("VKID.OneTap instance created");

				// 3. Подписка на успешное событие
				oneTap.on(VKID.OneTapInternalEvents.LOGIN_SUCCESS, (data: VKID.AuthResponse) => {
					log.info("VK One-Tap login success event received");
					if (codeVerifierRef.current) {
						onSuccess({ payload: data, codeVerifier: codeVerifierRef.current });
					} else {
						log.error("codeVerifier is missing after VK login success");
						onError(new Error("VK Auth failed: codeVerifier is missing."));
					}
				});

				// Подписка на ошибку
				oneTap.on(VKID.WidgetEvents.ERROR, (error: unknown) => {
					log.error("VK One-Tap widget error", error);
					onError(new Error("VK widget error."));
				});

				// 4. Рендеринг кнопки
				const container = document.getElementById(VK_CONTAINER_ID);
				if (container) {
					const oneTapConfig: OneTapParams = {
						container,
						scheme: VKID.Scheme.DARK
					};

					if (showOkLogin || showMailRuLogin) {
						oneTapConfig.showAlternativeLogin = true;
						oneTapConfig.oauthList ||= [];
						if (showOkLogin) {
							oneTapConfig.oauthList.push(VKID.OAuthName.OK);
						}
						if (showMailRuLogin) {
							oneTapConfig.oauthList.push(VKID.OAuthName.MAIL);
						}
					}

					oneTap.render(oneTapConfig);
					log.debug("VKID.OneTap button rendered");
				} else {
					log.error(`VK One-Tap container with id #${VK_CONTAINER_ID} not found`);
					onError(new Error("VK container not found."));
				}
			} catch (e) {
				log.error("Failed to initialize VK ID SDK", e);
				onError(e instanceof Error ? e : new Error("Unknown error during VK ID init."));
			}
		};

		void initializeVkId();

		return () => {
			isMounted = false;
			log.debug("VkOneTapButton unmounted, destroying VK ID widget");
			oneTapRef.current?.close();
		};
	}, [appId]); // Переинициализируем только если изменится appId

	// Рендерим контейнер, в который VK SDK добавит кнопку.
	return <div id={VK_CONTAINER_ID} style={{ width: "100%" }} />;
};
